/**
 * QR ordonnance — contenu lisible au scan.
 * FUNTOA SMIE. ORDONNANCE
 * Nom · Société
 * Médicament - posologie - quantité
 */

// require: interop fiable avec Jest / CRA (qrcode = CJS)
const QRCode = require('qrcode');

function cleanPart(value) {
  const text = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  return text || null;
}

function formatMedicationQrLine({ name, posology, quantity } = {}) {
  const medName = cleanPart(name) || 'Médicament';
  const poso = cleanPart(posology) || '—';
  const qty = cleanPart(quantity) || '—';
  return `${medName} - ${poso} - ${qty}`;
}

/**
 * @param {{
 *   patientName?: string|null,
 *   companyName?: string|null,
 *   medications?: Array<{ name?: string, posology?: string, quantity?: string }>
 * }} input
 */
export function buildPrescriptionQrPayload({
  patientName = null,
  companyName = null,
  medications = []
} = {}) {
  const lines = ['FUNTOA SMIE. ORDONNANCE'];

  const name = cleanPart(patientName);
  const company = cleanPart(companyName);
  if (name || company) {
    lines.push([name, company].filter(Boolean).join(' · '));
  }

  const medLines = (medications || [])
    .map((med) => formatMedicationQrLine(med))
    .filter(Boolean);

  if (!name && !company && !medLines.length) {
    return null;
  }

  if (medLines.length) {
    lines.push(...medLines);
  }

  return lines.join('\n');
}

export async function renderPrescriptionQrDataUrl(payload, { size = 128, margin = 1 } = {}) {
  if (!payload || typeof QRCode?.toDataURL !== 'function') return null;
  try {
    return await QRCode.toDataURL(payload, {
      errorCorrectionLevel: 'M',
      margin,
      width: size,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
  } catch {
    return null;
  }
}

function isDarkModule(modules, col, row, size) {
  if (typeof modules.get === 'function') {
    return Boolean(modules.get(col, row));
  }
  if (modules.data) {
    return Boolean(modules.data[row * size + col]);
  }
  return false;
}

/** Matrice booléenne pour dessin canvas thermique (true = module noir). */
export function renderPrescriptionQrMatrix(payload, { quietZone = 2 } = {}) {
  if (!payload || typeof QRCode?.create !== 'function') return null;
  try {
    const qr = QRCode.create(payload, {
      errorCorrectionLevel: 'M'
    });
    const size = qr.modules.size;
    const total = size + quietZone * 2;
    const matrix = Array.from({ length: total }, () => Array(total).fill(false));
    for (let row = 0; row < size; row += 1) {
      for (let col = 0; col < size; col += 1) {
        matrix[row + quietZone][col + quietZone] = isDarkModule(qr.modules, col, row, size);
      }
    }
    return matrix;
  } catch {
    return null;
  }
}

export function drawQrMatrix(ctx, matrix, x, y, drawSizePx) {
  if (!matrix?.length) return;
  const modules = matrix.length;
  const moduleSize = drawSizePx / modules;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(x, y, drawSizePx, drawSizePx);
  ctx.fillStyle = '#000000';
  for (let row = 0; row < modules; row += 1) {
    for (let col = 0; col < modules; col += 1) {
      if (!matrix[row][col]) continue;
      ctx.fillRect(
        x + col * moduleSize,
        y + row * moduleSize,
        Math.ceil(moduleSize),
        Math.ceil(moduleSize)
      );
    }
  }
}
